import React, { useRef, useState } from 'react';
import { alpha, Box, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

export const CompleteTaskButton = ({
  id,
  onClick,
  disabled = false,
  loading = false,
  compact = false,
  ariaLabel = 'Complete task',
}) => {
  const scale = compact ? 0.625 : 1;
  return (
    <IconButton
      id={id}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      sx={{
        width: 52 * scale,
        height: 48 * scale,
        color: 'background.default',
        bgcolor: 'green.main',
        border: `${compact ? 2 : 3}px solid`,
        borderColor: 'green.main',
        borderRadius: '50%',
        boxShadow: compact
          ? '0 4px 0 #187407, 0 0 10px rgba(57, 255, 20, 0.45)'
          : '0 6px 0 #187407, 0 0 16px rgba(57, 255, 20, 0.45)',
        transition: 'transform 140ms ease, box-shadow 140ms ease',
        '&:hover': {
          bgcolor: 'green.main',
          transform: compact ? 'translate(-1px, -1px)' : 'translate(-2px, -2px)',
          boxShadow: compact
            ? '1px 5px 0 #187407, 0 0 14px rgba(57, 255, 20, 0.6)'
            : '2px 8px 0 #187407, 0 0 22px rgba(57, 255, 20, 0.6)',
        },
        '&:active': {
          transform: compact ? 'translateY(2px)' : 'translateY(4px)',
          boxShadow: compact
            ? '0 1px 0 #187407, 0 0 7px rgba(57, 255, 20, 0.4)'
            : '0 2px 0 #187407, 0 0 10px rgba(57, 255, 20, 0.4)',
        },
        '&.Mui-disabled': {
          bgcolor: 'green.main',
          color: 'background.default',
          opacity: 0.55,
        },
      }}
    >
      {loading ? (
        <CircularProgress color="inherit" size={compact ? 14 : 20} thickness={6} />
      ) : (
        <CheckIcon sx={{ fontSize: compact ? 17 : 24 }} />
      )}
    </IconButton>
  );
};

export const LoadingIndicator = ({ text = 'LOADING...' }) => {
  const displayText = String(text || 'LOADING...').toUpperCase();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <Box sx={{ position: 'relative', display: 'flex' }}>
        <CircularProgress
          size={60}
          thickness={5}
          sx={{
            color: 'primary.main',
            filter: 'drop-shadow(0 0 10px #00E5FF)',
            animationDuration: '1.5s',
          }}
        />
        <CircularProgress
          size={60}
          thickness={2}
          sx={{
            color: 'secondary.main',
            filter: 'drop-shadow(0 0 8px #FF2D95)',
            position: 'absolute',
            left: 0,
            animationDuration: '0.5s',
            animationDirection: 'reverse',
          }}
        />
      </Box>
      <Typography
        sx={{
          color: 'secondary.main',
          fontSize: '0.75rem',
          textShadow: '0 0 10px #FF2D95',
          '@keyframes blinkerBase': {
            '0%, 49%': { opacity: 1 },
            '50%, 100%': { opacity: 0 },
          },
          animation: 'blinkerBase 1s infinite steps(1)',
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
};

/**
 * A touch-friendly equalizer scale. `increments` controls both the number of
 * selectable positions and the number of bars; their heights are distributed
 * evenly between `minHeight` and `maxHeight`.
 */
export const EqBoosterSlider = ({
  value,
  onChange,
  min = 1,
  max = 10,
  increments = 10,
  minHeight = 14,
  maxHeight = 48,
  ariaLabel = 'Value',
}) => {
  const [hoverValue, setHoverValue] = useState(null);
  const containerRef = useRef(null);
  const theme = useTheme();
  const safeIncrements = Math.max(2, Math.round(increments));
  const safeValue = Math.min(max, Math.max(min, Number(value) || min));
  const valueForIndex = (index) => min + ((max - min) * index) / (safeIncrements - 1);
  const indexForValue = (nextValue) => Math.round(
    ((nextValue - min) / (max - min || 1)) * (safeIncrements - 1)
  );
  const setFromIndex = (index) => onChange(valueForIndex(Math.min(safeIncrements - 1, Math.max(0, index))));

  const valueFromPointer = (event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect?.width) return safeValue;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    return valueForIndex(Math.round(ratio * (safeIncrements - 1)));
  };

  const updateFromPointer = (event) => onChange(valueFromPointer(event));
  const displayedValue = hoverValue ?? safeValue;
  const currentIndex = indexForValue(safeValue);
  const displayIndex = indexForValue(displayedValue);
  const activeEnd = Math.max(currentIndex, displayIndex);
  const previewStart = Math.min(currentIndex, displayIndex);

  const handleKeyDown = (event) => {
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') nextIndex -= 1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') nextIndex += 1;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = safeIncrements - 1;
    else return;

    event.preventDefault();
    setFromIndex(nextIndex);
  };

  return (
    <Box
      ref={containerRef}
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={safeValue}
      onKeyDown={handleKeyDown}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        updateFromPointer(event);
      }}
      onPointerMove={(event) => {
        const nextValue = valueFromPointer(event);
        if (event.pointerType === 'mouse' && event.buttons !== 1) {
          setHoverValue(nextValue);
        } else {
          onChange(nextValue);
        }
      }}
      onPointerLeave={() => setHoverValue(null)}
      onPointerUp={(event) => event.currentTarget.releasePointerCapture?.(event.pointerId)}
      onPointerCancel={() => setHoverValue(null)}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.75,
        height: maxHeight,
        width: '100%',
        cursor: 'pointer',
        touchAction: 'none',
        outline: 'none',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 5,
          borderRadius: 1,
        },
      }}
    >
      {Array.from({ length: safeIncrements }, (_, index) => {
        const isActive = index <= activeEnd;
        const isPreview = hoverValue !== null && index > previewStart && index <= activeEnd;
        const height = minHeight + ((maxHeight - minHeight) * index) / (safeIncrements - 1);

        return (
          <Box
            key={index}
            sx={{
              flex: 1,
              height,
              minWidth: 0,
              bgcolor: isActive ? 'secondary.main' : alpha(theme.palette.text.primary, 0.12),
              boxShadow: isActive && !isPreview
                ? `0 0 8px ${alpha(theme.palette.secondary.main, 0.8)}`
                : 'none',
              opacity: isPreview ? 0.4 : 1,
              transition: 'background-color 75ms ease, box-shadow 75ms ease, opacity 75ms ease',
            }}
          />
        );
      })}
    </Box>
  );
};
